import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { getErrorMessage } from '../../utils/apiClient';

/**
 * #201334 (lệnh sếp update_id=731600902, khuyến nghị Điều tra #194925) — từ chối 1 ca đăng ký
 * BẮT BUỘC nhập lý do. Lý do lưu vào ca (máy chủ cũng chặn nếu thiếu) và nhân viên đọc được trong
 * app Ồn Staff, tab "Ca của tôi".
 *
 * Ô lý do là chữ TỰ DO — không có danh sách lý do soạn sẵn, không mớm câu mẫu (luật sếp
 * update_id=731598901/731598906 cho mọi ô lý do). Cỡ chữ 16px để Safari trên iPhone không tự
 * phóng to khi chạm vào ô.
 *
 * #222214 (lỗi Nhẹ Kiểm thử #210028): lỗi "Bắt buộc nhập lý do" CHỈ bật khi bấm "Xác nhận từ chối",
 * KHÔNG bật lúc ô mất con trỏ — trước đây bấm chuột vào "Hủy" làm ô mất con trỏ ⇒ hiện dòng đỏ ⇒ hộp
 * cao thêm, nút Hủy tụt xuống dưới chuột ⇒ phải bấm Hủy 2 lần. Nếu `onConfirm` trả Promise thì hộp
 * GIỮ MỞ tới khi gửi xong: gửi lỗi (vd mất mạng) thì báo lỗi ngay trong hộp, lý do vừa gõ còn nguyên.
 */
export const SHIFT_REJECT_REASON_MAX_LENGTH = 500;

const RejectShiftReasonModal = ({ show, shiftLabel, onCancel, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (show) {
      setReason('');
      setTouched(false);
      setSubmitting(false);
      setSubmitError('');
    }
  }, [show]);

  const trimmed = reason.trim();
  const invalid = trimmed.length === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setTouched(true);
    if (invalid) return;
    setSubmitError('');
    setSubmitting(true);
    try {
      await onConfirm(trimmed);
    } catch (error) {
      // Không có phản hồi từ máy chủ (mất mạng) thì axios chỉ nói "Network Error" — đổi sang câu dễ hiểu.
      const noResponse = !error?.response && !error?.status;
      setSubmitError(
        noResponse
          ? 'Không gửi được vì mất kết nối mạng. Lý do vẫn giữ nguyên — kiểm tra mạng rồi bấm "Xác nhận từ chối" lại.'
          : getErrorMessage(error) || 'Từ chối ca thất bại, vui lòng thử lại.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Đang gửi thì không cho đóng hộp (bấm nền/dấu X/Esc) — tránh mất lý do giữa chừng.
  const handleHide = () => {
    if (!submitting) onCancel();
  };

  return (
    <Modal show={show} onHide={handleHide} centered data-testid="reject-shift-reason-modal">
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>Từ chối ca</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {shiftLabel && <p className="text-muted small mb-3">{shiftLabel}</p>}
          <Form.Group controlId="reject-shift-reason">
            <Form.Label>
              Lý do từ chối <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={SHIFT_REJECT_REASON_MAX_LENGTH}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              isInvalid={touched && invalid}
              disabled={submitting}
              autoFocus
              style={{ fontSize: 16 }}
              data-testid="reject-shift-reason-input"
            />
            <Form.Control.Feedback type="invalid">
              Bắt buộc nhập lý do để nhân viên biết vì sao ca bị từ chối.
            </Form.Control.Feedback>
            <Form.Text muted>Nhân viên sẽ xem được lý do này trong app Ồn Staff.</Form.Text>
          </Form.Group>
          {submitError && (
            <Alert variant="danger" className="mt-3 mb-0" data-testid="reject-shift-reason-error">
              {submitError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            Hủy
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={submitting || (touched && invalid)}
            data-testid="reject-shift-reason-submit"
          >
            {submitting && <Spinner as="span" animation="border" size="sm" className="me-2" />}
            Xác nhận từ chối
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

/**
 * Hỏi lý do từ chối theo kiểu "chờ trả lời": `await askRejectReason(nhãn ca)` trả chuỗi lý do
 * (đã cắt khoảng trắng) hoặc `null` nếu người dùng bấm Hủy. Nhờ vậy mọi nút "Từ chối" cũ chỉ cần
 * gọi lại ĐÚNG hàm từ chối sẵn có — không phải sửa từng nơi gọi.
 *
 * Truyền thêm `submit(lý do)` (hàm trả Promise gọi máy chủ) thì hộp giữ mở tới khi `submit` xong
 * (#222214): lỗi thì hộp báo lỗi và chờ bấm lại, thành công mới đóng và trả lý do.
 */
export function useRejectShiftReason() {
  const [state, setState] = useState({ show: false, shiftLabel: '' });
  const resolverRef = useRef(null);

  const settle = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState({ show: false, shiftLabel: '' });
    if (resolve) resolve(value);
  }, []);

  const submitRef = useRef(null);

  const askRejectReason = useCallback((shiftLabel = '', submit = null) => {
    // Hộp cũ chưa trả lời mà mở hộp mới thì coi hộp cũ là Hủy, không để lời hứa treo mãi.
    if (resolverRef.current) resolverRef.current(null);
    submitRef.current = submit;
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ show: true, shiftLabel });
    });
  }, []);

  const confirm = useCallback(
    async (reason) => {
      if (submitRef.current) await submitRef.current(reason);
      settle(reason);
    },
    [settle]
  );

  const rejectReasonModal = (
    <RejectShiftReasonModal
      show={state.show}
      shiftLabel={state.shiftLabel}
      onCancel={() => settle(null)}
      onConfirm={confirm}
    />
  );

  return { askRejectReason, rejectReasonModal };
}

export default RejectShiftReasonModal;
